import cn from 'classnames'
import React, { FC, InputHTMLAttributes } from 'react'

// Explicitly separate our custom props from native input props
type NativeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'type' | 'checked'
>

type CheckboxProps = NativeInputProps & {
  label?: string
  id: string
  checked: boolean
  onChange?: (newValue: boolean) => void
}

const CheckedIcon: FC = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path
      d='M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22Z'
      fill='#1977EC'
    />
    <path
      d='M10.243 16.485L6 12.242L7.414 10.828L10.243 13.657L15.899 8L17.314 9.414L10.243 16.485Z'
      fill='white'
    />
  </svg>
)

const UncheckedIcon: FC = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <circle cx='12' cy='12' r='9.5' stroke='#1D1D1F' />
  </svg>
)

const Checkbox: FC<CheckboxProps> = ({
  checked,
  className,
  disabled = false,
  id,
  label,
  name,
  onChange,
  ...rest
}) => {
  const handleToggle = () => {
    if (!disabled) {
      onChange?.(!checked)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      handleToggle()
    }
  }

  return (
    <div
      role='checkbox'
      aria-checked={checked}
      aria-disabled={disabled}
      aria-labelledby={label ? `${id}-label` : undefined}
      tabIndex={disabled ? -1 : 0}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      className={cn(
        'lh-input d-flex gap-1 align-items-center user-select-none',
        {
          'cursor-pointer': !disabled,
          'opacity-50 cursor-not-allowed': disabled,
        },
        className,
      )}
    >
      {/* Hidden native input for form compatibility */}
      <input
        type='checkbox'
        id={id}
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={() => {}} // Controlled by parent via custom onChange
        tabIndex={-1}
        className='position-absolute opacity-0 pointer-events-none'
        {...rest}
      />

      {checked ? <CheckedIcon /> : <UncheckedIcon />}

      {label && (
        <label
          id={`${id}-label`}
          className='form-check-label cursor-pointer'
          htmlFor={id}
        >
          {label}
        </label>
      )}
    </div>
  )
}

Checkbox.displayName = 'Checkbox'
export default Checkbox
